import {
    Button,
    ButtonGroup,
    Card,
    CardBody,
    Flex,
    Stack,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Text,
} from '@chakra-ui/react';
import { startCase } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { usePortfolios } from '@/cip/api/portfolio';

import { AlgoAction, Portfolio, RunMode } from '../../_openapi/portfolio-optimizer';
import { useSubmitOptimization } from '../../api/optimization';
import { formatDate, lastBusinessDay, SERVER_DATE_FORMAT } from '../../utils/dateUtils';
import { DatePicker } from '../../widgets/DatePicker';
import { PageHeader } from '../../widgets/PageHeader';
import { SegmentedControls } from '../../widgets/SegmentedControls';
import { TradeReasonSelector, useTradeReasonState } from '../../widgets/TradeReasonSelector';
import { AllocationType } from '../allocation_types';
import { AllocationTypeSelector } from './OptimizationPlan/AllocationTypeSelector';
import { BrokerAccountRestrictions } from './OptimizationPlan/BrokerAccountRestrictions';
import { OptimizationPortfolioSelector } from './OptimizationPlan/OptimizationPortfolioSelector';
import { useOptimizationPrePopulation } from './useOptimizationPrePopulation';

export const OptimizationBuilder = () => {
    const [searchParams] = useSearchParams();
    const rerunOptimizationId = searchParams.get('rerun');

    return (
        <>
            <PageHeader
                title={rerunOptimizationId ? `Rerun Optimization #${rerunOptimizationId}` : 'Optimization builder'}
            />
            <OptimizationBuilderPage />
        </>
    );
};

const OptimizationBuilderPage = () => {
    const navigate = useNavigate();

    const { mutate: submitOptimization, isPending } = useSubmitOptimization();
    const { data: portfoliosData } = usePortfolios({
        dataVariant: 'preTrade',
        columnFilters: [
            {
                id: 'isSuspended',
                value: false,
            },
        ],
    });

    const [dataAsOfDate, setDataAsOfDate] = useState<Date>(lastBusinessDay());
    const [tradeDateOverride, setTradeDateOverride] = useState<Date | undefined>();
    const { reason, ...tradeReasonState } = useTradeReasonState();

    const [runMode, setRunMode] = useState<RunMode>(RunMode.REBALANCE);
    const [selectedAlgoAction, setSelectedAlgoAction] = useState<AlgoAction>();
    const [selectedAllocationTypes, setSelectedAllocationTypes] = useState<AllocationType[]>([]);
    const [includedPortfolios, setIncludedPortfolios] = useState<Portfolio[]>([]);
    const [excludedPortfolios, setExcludedPortfolios] = useState<Portfolio[]>([]);

    // Initialize all portfolios in exclusions list only once
    useEffect(() => {
        if (portfoliosData?.portfolios && excludedPortfolios.length === 0 && includedPortfolios.length === 0) {
            setExcludedPortfolios(portfoliosData.portfolios);
        }
    }, [portfoliosData?.portfolios, excludedPortfolios.length, includedPortfolios.length]);

    // Handle pre-population from rerun optimization or investment requests
    useOptimizationPrePopulation({
        portfoliosData,
        setDataAsOfDate,
        setTradeDateOverride,
        setSelectedAlgoAction,
        setSelectedAllocationTypes,
        setSelectedReason: tradeReasonState.setSelectedReason,
        setIncludedPortfolios,
        setExcludedPortfolios,
    });

    const onInvert = useCallback(() => {
        setIncludedPortfolios(excludedPortfolios);
        setExcludedPortfolios(includedPortfolios);
    }, [includedPortfolios, excludedPortfolios]);

    const onSubmit = () => {
        const accountGuids = includedPortfolios
            .map((portfolio) => portfolio.base.portfolioGuid)
            .filter((guid): guid is string => Boolean(guid));

        const missingGuidCount = includedPortfolios.length - accountGuids.length;
        if (missingGuidCount > 0) {
            toast.warning(
                `${missingGuidCount} included portfolio${missingGuidCount > 1 ? 's have' : ' has'} no account GUID and will be excluded from this optimization.`,
            );
        }

        submitOptimization(
            {
                details: reason,
                dataAsOfDate: formatDate(dataAsOfDate, SERVER_DATE_FORMAT)!,
                tradeDateOverride: tradeDateOverride ? formatDate(tradeDateOverride, SERVER_DATE_FORMAT) : undefined,
                algoAction: selectedAlgoAction!,
                runMode,
                allocationTypes: selectedAllocationTypes,
                accountGuids,
            },
            {
                onSuccess: () => {
                    navigate('..');
                },
            },
        );
    };

    return (
        <Card>
            <CardBody>
                <Stack>
                    <Flex gap={6}>
                        <Stack flex={1} gap={6}>
                            <TradeReasonSelector {...tradeReasonState} />

                            <Flex justifyContent="space-between">
                                <Flex alignItems="center" gap={4}>
                                    <Text fontWeight="medium">Data As of Date</Text>
                                    <Flex width="fit-content" minW="160px">
                                        <DatePicker
                                            onChange={(date) => setDataAsOfDate(date as Date)}
                                            value={dataAsOfDate}
                                        />
                                    </Flex>
                                </Flex>

                                <Flex alignItems="center" gap={4}>
                                    <Text fontWeight="medium">Trade Date Override</Text>
                                    <Flex width="fit-content" minW="160px">
                                        <DatePicker
                                            onChange={(date) => setTradeDateOverride(date as Date)}
                                            value={tradeDateOverride}
                                        />
                                    </Flex>
                                </Flex>
                            </Flex>

                            <Tabs>
                                <TabList>
                                    <Tab>Inclusions ({includedPortfolios.length})</Tab>
                                    <Tab>Exclusions ({excludedPortfolios.length})</Tab>
                                </TabList>
                                <TabPanels>
                                    <TabPanel>
                                        <Stack gap={4}>
                                            <BrokerAccountRestrictions portfolios={includedPortfolios} />
                                            <OptimizationPortfolioSelector
                                                includedPortfolios={includedPortfolios}
                                                setCurrentPortfolios={setIncludedPortfolios}
                                                setOtherPortfolios={setExcludedPortfolios}
                                                listType="inclusions"
                                                portfoliosData={portfoliosData}
                                                onInvert={onInvert}
                                            />
                                        </Stack>
                                    </TabPanel>
                                    <TabPanel>
                                        <OptimizationPortfolioSelector
                                            includedPortfolios={excludedPortfolios}
                                            setCurrentPortfolios={setExcludedPortfolios}
                                            setOtherPortfolios={setIncludedPortfolios}
                                            listType="exclusions"
                                            portfoliosData={portfoliosData}
                                            onInvert={onInvert}
                                        />
                                    </TabPanel>
                                </TabPanels>
                            </Tabs>
                        </Stack>
                        <Stack w="340px" flexShrink={0}>
                            <Stack px={5} pb={4}>
                                <Text fontWeight="medium">Run mode</Text>
                                <SegmentedControls
                                    stretch
                                    options={[
                                        { value: RunMode.REBALANCE, label: 'Rebalance' },
                                        { value: RunMode.WITHDRAWAL, label: 'Withdrawal' },
                                    ]}
                                    selectedValue={runMode}
                                    onClick={(mode) => {
                                        setRunMode(mode);
                                        if (mode === RunMode.WITHDRAWAL) {
                                            setSelectedAlgoAction(AlgoAction.SELL);
                                        }
                                    }}
                                />
                                {runMode === RunMode.WITHDRAWAL && (
                                    <Text color="orange.500" fontSize="sm">
                                        Sells only raise the accounts&apos; uncovered cash needs - no TLH, growth
                                        allocations only.
                                    </Text>
                                )}
                            </Stack>
                            <Stack px={5} pb={4}>
                                <Text fontWeight="medium">Algo action</Text>
                                <SegmentedControls
                                    stretch
                                    options={[
                                        {
                                            value: AlgoAction.BUY,
                                            label: startCase(AlgoAction.BUY),
                                            disabled: runMode === RunMode.WITHDRAWAL,
                                        },
                                        { value: AlgoAction.SELL, label: startCase(AlgoAction.SELL) },
                                    ]}
                                    selectedValue={selectedAlgoAction}
                                    onClick={setSelectedAlgoAction}
                                />
                            </Stack>
                            <AllocationTypeSelector
                                selectedAlgoAction={selectedAlgoAction}
                                selectedAllocationTypes={selectedAllocationTypes}
                                setSelectedAllocationTypes={setSelectedAllocationTypes}
                            />
                        </Stack>
                    </Flex>

                    <ButtonGroup ml="auto" mt={2}>
                        <Button as={RouterLink} to=".." variant="outline" isDisabled={isPending}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            isLoading={isPending}
                            isDisabled={
                                !reason.length ||
                                !dataAsOfDate ||
                                !selectedAlgoAction ||
                                !selectedAllocationTypes.length ||
                                !includedPortfolios.length
                            }
                            onClick={onSubmit}
                        >
                            Submit
                        </Button>
                    </ButtonGroup>
                </Stack>
            </CardBody>
        </Card>
    );
};
